<script setup>
import {defineProps, computed, watch, defineEmits, onMounted, ref, nextTick} from 'vue'
import Jumbo from '@/components/Docs/Jumbo.vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'

import StringParam from '@/components/Docs/Parameters/StringParam.vue'
import RequestRunner from '@/components/Docs/RequestRunner.vue'

import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'

let request_hash = ref("21109_2_1460_0")
let request_offset = ref("")
let request_limit = ref("")

let get_query = computed(() => {
  var query = "/query/fingerprint/" + request_hash.value 
  
  if (request_limit.value != "" || request_offset.value != "") query += "?"
  
  if (request_limit.value != "") {
    query += "limit="+request_limit.value
  }
  if (request_offset.value != "") {
    if (request_limit.value != "") query += "&"
    query += "offset="+request_offset.value
  }

  return query
}) 

</script>

<template>
  <div>
    <doc-section title="Fingerprint lookup">
      <template v-slot:left>
        <div class="mb-5">
          <div class="mb-3">
This endpoint allows for retrieving ip addresses associated with a given hash or fingerprint. It only supports exact matches. Webscout currently supports the full JA4+ and JA3 suite of fingerprints.
          </div>

        </div>
      
        <div class="mb-5">
          <h4 class="mb-4">Request parameters</h4>
          <StringParam class="mb-3" name="Fingerprint" :required="true" description="The fingerprint you wish to lookup" type="string" :value="request_hash" @value="(v) => request_hash = v" />

          <StringParam class="mb-3" name="offset" description="The amount of ips you have already retrieved" type="integer" :value="request_offset" @value="(v) => request_offset = v" />
          <StringParam class="mb-3" name="limit" description="The amount of ips returned on each request. Defaults to 100 which is also the maximum" type="integer" :value="request_limit" @value="(v) => request_limit = v" />

          <RequestRunner class="mt-5" :query="get_query" name="Domain information" method="GET"></RequestRunner>
        </div>

        <div>
          <h4 class="mb-4">Response attributes</h4>


          <BasicAttribute name="status" type="string" description="The status of the request's response" />
          <hr>

          <BasicAttribute name="data" type="object array" description="The requested information">

            <BasicAttribute name="risk_score" type="integer" description="Webscout's determination of the risk associated with the IP. Displayed in the form of a number between 0-100." />
            <hr>

            <BasicAttribute name="ip" type="string" description="The target IP address" />
            <hr>

            <BasicAttribute name="location" type="object" description="The location of the ip">
              <BasicAttribute name="country_iso" type="string" description="Two letter country code" />
              <hr>
              <BasicAttribute name="city" type="string" description="The full name of the city" />
            </BasicAttribute>

            <BasicAttribute name="anonymization" type="object" description="">
              <BasicAttribute name="vpn" type="boolean" description="whether or not the ip has been used as a vpn" />
              <hr>
              <BasicAttribute name="proxy" type="boolean" description="whether or not the ip has been used as a proxy" />
              <hr>
              <BasicAttribute name="tor" type="boolean" description="whether or not the ip is involved in the tor network" />
              <hr>
              <BasicAttribute name="service" type="string" description="The name of the operator of the anonymization service" />
            </BasicAttribute>

            <BasicAttribute name="network" type="object" description="">
              <BasicAttribute name="type" type="string" description="The likely type of infrastructure used to operate the ip" />
              <hr>
              <BasicAttribute name="service" type="string" description="The service the ip is a part of. For instance S3, cdn.." />
              <hr>
              <BasicAttribute name="region" type="string" description="This will usually map to a cloud providers regional mappings" />
              <hr>
              <BasicAttribute name="risk_score" type="integer" description="The risk_score assosiated with the network range" />
              <hr>
              <BasicAttribute name="range" type="string" description="The closest allocation of addresses assosiated with the ip address" />
              <hr>
              <BasicAttribute name="private" type="boolean" description="whether or not the ip belongs to a reversed network space" />
            </BasicAttribute>

            <BasicAttribute name="as" type="object" description="">
              <BasicAttribute name="as_numbers" type="integer array" description="The AS number(s) of the organization owning the ip address allocation" />
              <hr>
              <BasicAttribute name="organization" type="string" description="The name of the organization owning the ip address allocation" />
            </BasicAttribute>

            <BasicAttribute name="hostnames" type="string array" description="List of hostnames announced by the IP or operator" />
            <hr>

            <BasicAttribute name="domains_on_ip" type="integer" description="The number of domains resolving to the IP" />
            <hr>

            <BasicAttribute name="company" type="object" description="">
              <BasicAttribute name="name" type="string" description="The entity managing the ip address" />
              <hr>
              <BasicAttribute name="business" type="string array" description="A list of services the company provides as part of it's business" />
              <hr>
              <BasicAttribute name="description" type="string" description="A brief description of who the network provider is" />
            </BasicAttribute>

            <BasicAttribute name="behavior" type="object" description="">
              <BasicAttribute name="tags" type="string array" description="Activity assosiated with the IP address" />
              <hr>
              <BasicAttribute name="open_ports" type="integer array" description="List of open ports on the system, likely denoting its purpose" />
              <hr>
              <BasicAttribute name="last_seen" type="string" description="When we last observed behavior from this ip" />
            </BasicAttribute>

            <BasicAttribute name="osint" type="object" description="">
              <BasicAttribute name="tags" type="string array" description="Activity assosiated with the IP address as reported in public sources" />
              <hr>
              <BasicAttribute name="first_seen" type="string" description="When we first observed reporting on this ip" />
              <hr>
              <BasicAttribute name="last_seen" type="string" description="When we last observed reporting on this ip" />
            </BasicAttribute>
  
          </BasicAttribute>


        </div>

      </template>

      <template v-slot:right>
        <code-block title="/query/fingerprint/:fingerprint" method="GET" dark/>
      
        <json-block title="Response">
{
"status": 
"success",
"data": 
[
{
"risk_score": 
100,
"ip": 
"207.90.244.14",
"location": 
{
"country_iso": 
"US",
"city": 
"Pflugerville"
},
"anonymization": 
{
"vpn": 
false,
"proxy": 
false,
"tor": 
false,
"service": 
""
},
"network": 
{
"type": 
"Cloud",
"service": 
"",
"region": 
"",
"risk_score": 
4,
"range": 
"207.90.244.0/24",
"private": 
false
},
"as": 
{
"organization": 
"SHODAN, LLC",
"as_numbers": 
[
174
]
},
"hostnames": 
null,
"domains_on_ip": 
0,
"company": 
{
"name": 
"SHODAN",
"business": 
[
"Cloud",
"Security"
],
"description": 
"A search engine for Internet-connected devices, showcasing various web services and security aspects."
},
"behavior": 
{
"tags": 
[
"bruteforce",
"bruteforce_login",
"bruteforce_telnet",
"probing",
"probing_http",
"probing_https",
"probing_imap",
"probing_kafka",
"probing_ldap",
"probing_mysql",
"probing_rdp",
"probing_redis",
"probing_rpc",
"probing_smb",
"probing_telnet",
"probing_vnc",
"scanning",
"scanning_dns_tcp",
"scanning_http",
"scanning_https",
"scanning_port_9779",
"scanning_port_9802",
"scanning_port_9872",
"scanning_port_99",
"scanning_port_9929",
"scanning_port_9939",
"scanning_port_9966",
"scanning_port_998",
"scanning_port_9992",
"scanning_smtp",
"scanning_ssh",
"scanning_vnc"
],
"open_ports": 
[
22
],
"last_seen": 
"2025-06-30T09:03:59Z"
},
"osint": 
{
"tags": 
[
"malicious"
],
"first_seen": 
"2025-04-02T04:40:48.160491Z",
"last_seen": 
"2025-06-30T08:09:58.921152Z"
}
}
]
}
        </json-block>
      </template>
    </doc-section>
  </div>
</template>

<style scoped>
  
.table {
  width: 100%;
  table-layout: fixed;
}


.table th {
  color: white;
  font-weight: normal
}

.table tr {
  border-bottom: 1px solid #29323E;
}

.table tbody tr:last-child {
  border-bottom: 1px solid transparent;
}

.table td {
  color: #6D7785;
}

.table .first {
  width: 3.5rem;
}

.table .second {
  width: 10rem;
}
</style>
